import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { urlValue, unfolder, useTranslate, openAlert } from '../lib';

const PromptTemplateModal = ({ isOpen, onClose, onSelect, hideCreate = false }) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({ id: null, name: '', content: '', additional_object: { example: '' } });
    const [showForm, setShowForm] = useState(false);
    const { t } = useTranslate();

    // Obtención de credenciales
    const storedHashInfo = localStorage.getItem('hashInfo');
    const storedHashUser = localStorage.getItem('hashUser');
    const currentUserId = unfolder(storedHashInfo, 'IANestingKey')?.id;
    const token = unfolder(storedHashUser, 'IANestingKey')?.token;

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            if (!currentUserId || !token) return;

            const response = await axios({
                method: 'get',
                url: `${urlValue}/studio/prompt/library/templates`,
                headers: { 'user': currentUserId, 'access-token': token }
            });

            if (response.data) {
                setTemplates(Array.isArray(response.data) ? response.data : []);
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchTemplates();
            resetForm();
        }
    }, [isOpen]);

    const resetForm = () => {
        setFormData({ id: null, name: '', content: '', additional_object: { example: '' } });
        setShowForm(false);
    };

    const handleSaveTemplate = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.content) return alert("Completa todos los campos");

        try {
            setLoading(true);

            const contentContext = formData.content;

            await axios({
                method: 'post',
                url: `${urlValue}/studio/prompt/library/templates/save`,
                headers: { 'user': currentUserId, 'access-token': token },
                data: {
                    ...formData,
                    content: contentContext,
                    user_id: currentUserId
                }
            });
            
            resetForm();
            fetchTemplates();
        } catch (error) {
            console.error('Error saving template:', error);
            alert("Error al guardar la plantilla");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (template, e) => {
        e.stopPropagation(); // Evita que se seleccione la plantilla al querer editarla
        setFormData({ 
            id: template.id, 
            name: template.name, 
            content: template.content, 
            additional_object: template.additional_object || { example: '' } 
        });
        setShowForm(true);
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay-custom" style={styles.overlay} onClick={onClose}>
            <div className="modal-content-custom" style={styles.content} onClick={e => e.stopPropagation()}>
                
                <div style={styles.header}>

                    <div style={{ display: 'flex', alignItems: 'center'}}>
                        <img src='/icons/prompt-template-icon.png' style={{ width: 25, marginRight: 7 }} alt='' />
                        <h2 style={{ margin: 0, color: "#0B5FA3" }}>{t('common.templates')}</h2>
                    </div>
                    
                    
                    <div style={{display: 'flex', gap: '15px', alignItems: 'center'}}>
                        {!showForm && !hideCreate && (
                            <button 
                                style={styles.addBtn} 
                                onClick={() => setShowForm(true)}
                            >
                                {t('common.create') || 'Crear'}
                            </button>
                        )}
                        <button style={styles.closeBtn} onClick={onClose}>&times;</button>
                    </div>
                </div>

                <div style={styles.body}>
                    {showForm ? (
                        <form onSubmit={handleSaveTemplate} style={styles.form}>
                            <h3>{formData.id ? t('common.editTemplate') : t('common.newTemplate')}</h3>
                            <input 
                                style={styles.input}
                                placeholder={t('common.verticalName')}
                                value={formData.name}
                                onChange={e => setFormData({...formData, name: e.target.value})}
                                required
                            />
                            <textarea 
                                style={styles.textarea}
                                placeholder={t('common.xml_structure')}
                                value={formData.content}
                                onChange={e => setFormData({...formData, content: e.target.value})}
                                required
                                rows={10}
                            />
                            <textarea 
                                style={styles.textarea}
                                placeholder={t('menu.genai.example')}
                                value={formData.additional_object?.example || ''}
                                onChange={e => setFormData({
                                    ...formData, 
                                    additional_object: { 
                                        ...formData.additional_object, 
                                        example: e.target.value 
                                    }
                                })}
                                rows={4}
                            />
                            <div style={styles.formActions}>
                                <button type="button" onClick={resetForm} style={styles.cancelBtn}>{t('common.cancel')}</button>
                                <button type="submit" style={styles.saveBtn}>{t('common.saveTemplate')}</button>
                            </div>
                        </form>
                    ) : (
                        <>
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '20px' }}>{t('common.loading')}...</div>
                            ) : (
                                <div style={styles.grid}>
                                    {templates.length > 0 ? (
                                        templates.map((template) => (
                                            <div 
                                                key={template.id} 
                                                style={styles.card}
                                                onClick={() => onSelect(template.content, template.additional_object?.example)}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>{template.name}</h4>
                                                    {template.user_id === currentUserId && !hideCreate && (
                                                        <button 
                                                            onClick={(e) => handleEdit(template, e)}
                                                            style={styles.editIconBtn}
                                                            title={t('common.editTemplate')}
                                                        >
                                                            ✏️
                                                        </button>
                                                    )}
                                                </div>
                                                <p style={styles.previewText}>
                                                    {template.content.substring(0, 80)}...
                                                </p>
                                                <div style={{  
                                                    display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end'
                                                }}>
                                                    <img src='/icons/select_icon.png' style={{ width: 15 }} alt='' />
                                                    <small style={{ color: '#0B5FA3', fontWeight: 'bold' }}>
                                                        {t('common.select') || 'Seleccionar'}
                                                    </small>
                                                </div>
                                                
                                            </div>
                                        ))
                                    ) : (
                                        <p style={{ textAlign: 'center', width: '100%' }}>{t('common.noTemplates')}</p>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const styles = {
    overlay: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex',
        justifyContent: 'center', alignItems: 'center', zIndex: 1000
    },
    content: {
        backgroundColor: 'white', padding: '25px', borderRadius: '12px',
        width: '90%', maxWidth: '700px', maxHeight: '85vh', overflowY: 'auto',
        boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
    },
    header: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '1px solid #eee', paddingBottom: '15px', marginBottom: '15px'
    },
    closeBtn: { border: 'none', background: 'none', fontSize: '28px', cursor: 'pointer', color: '#999' },
    addBtn: { 
        backgroundColor: '#c4e3fc', color: '#0B5FA3', border: 'none', 
        padding: '5px 15px', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' 
    },
    body: { minHeight: '200px' },
    grid: {
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '15px'
    },
    card: {
        border: '1px solid #e0e0e0', borderRadius: '8px', padding: '15px',
        cursor: 'pointer', transition: 'all 0.2s', position: 'relative',
        backgroundColor: '#f9f9f9',
        '&:hover': { backgroundColor: '#f0f4ff', borderColor: '#0038A8' }
    },
    previewText: { fontSize: '11px', color: '#666', marginBottom: '10px', whiteSpace: 'pre-wrap' },
    form: { display: 'flex', flexDirection: 'column', gap: '10px' },
    input: { padding: '10px', borderRadius: '5px', border: '1px solid #ddd', fontSize: '14px' },
    textarea: { 
        padding: '10px', borderRadius: '5px', border: '1px solid #ddd', 
        fontSize: '13px', fontFamily: 'monospace', resize: 'vertical' 
    },
    formActions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' },
    saveBtn: { backgroundColor: '#0038A8', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '5px', cursor: 'pointer' },
    cancelBtn: { backgroundColor: '#eee', border: 'none', padding: '10px 20px', borderRadius: '5px', cursor: 'pointer' },
    editIconBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px' }
};

export default PromptTemplateModal;